// Skins — per-player side game, ported BYTE-FOR-BYTE from GolfGamesScorecard's
// computeSkins + skinsSettlement (app/scorecard.tsx) so the web and the
// companion app settle Skins IDENTICALLY. Runs alongside Wolf. NET when the
// game's "Handicaps (net)" is on — using the SAME stroke grid as the Wolf money
// (wolfStrokeGrid + the game's strokeMode), so the whole round shares one net
// model (this is the members' version; the app force-disabled Skins on net Wolf).
//
// Contract shape (WOLF_SYNC_CONTRACT.md): meta.addons.skins =
//   { enabled, amount, carry, double, payout:'per-player'|'split-pot', in:[playerId] }
import { wolfStrokeGrid } from './ggPlayWolf.js'

// VERBATIM port of the app's computeSkins. `grid` = per-player [0..17] scores
// (net or gross), null for unscored holes. `currentHole` is 0-based and only
// affects the live "pending" readout. Lowest UNIQUE score among IN players wins
// the hole; a tie carries (double → ×2, else +1) or burns (carry off). Returns
// { counts, holeWinner, valueAtHole, pending }.
export function computeSkins(names, grid, currentHole, inArr, carry, doubleOnCarry = false, played) {
  const n = names.length
  const counts = names.map(() => 0)
  const holeWinner = Array(18).fill(null)
  const valueAtHole = Array(18).fill(0)
  let pending = 1
  let pendingAtCurrent = 1
  for (let h = 0; h < 18; h++) {
    if (h === currentHole) pendingAtCurrent = pending
    // Skip a hole until every IN player has a real score for it (and, if a
    // `played` grid is supplied, has flipped played). Sparse web scores make
    // the numeric check alone the "hole complete" signal.
    let allScored = true
    for (let i = 0; i < grid.length; i++) {
      if (!inArr[i]) continue
      if (typeof grid[i]?.[h] !== 'number') { allScored = false; break }
      if (played && played[i]?.[h] !== true) { allScored = false; break }
    }
    if (!allScored) continue
    let minScore = Infinity
    let winners = []
    for (let i = 0; i < n; i++) {
      if (!inArr[i]) continue
      const s = grid[i][h]
      if (s < minScore) { minScore = s; winners = [i] }
      else if (s === minScore) winners.push(i)
    }
    if (winners.length === 1) {
      counts[winners[0]] += pending
      holeWinner[h] = winners[0]
      valueAtHole[h] = pending
      pending = 1
    } else {
      holeWinner[h] = 'tie'
      valueAtHole[h] = pending
      pending = carry ? (doubleOnCarry ? pending * 2 : pending + 1) : 1
    }
  }
  if (currentHole >= 18) pendingAtCurrent = pending
  return { counts, holeWinner, valueAtHole, pending: pendingAtCurrent }
}

// VERBATIM port of the app's skinsSettlement money block → per-player $ nets (by
// index). 'per-player': each losing IN player pays the full skin value to the
// winner. 'split-pot': the skin value is one pot split among the losing IN
// players. Sum of nets is 0.
export function settleSkins(result, amount, inArr, payout = 'split-pot') {
  const n = inArr.length
  const totals = Array(n).fill(0)
  if (!result || !(amount > 0)) return totals
  const inCount = inArr.filter(Boolean).length
  const others = inCount - 1
  if (others <= 0) return totals
  for (let h = 0; h < 18; h++) {
    const w = result.holeWinner[h]
    if (typeof w !== 'number') continue
    const skinValue = result.valueAtHole[h] * amount
    if (payout === 'per-player') {
      for (let i = 0; i < n; i++) {
        if (!inArr[i]) continue
        if (i === w) totals[i] += skinValue * others
        else totals[i] -= skinValue
      }
    } else {
      const perLoser = skinValue / others
      for (let i = 0; i < n; i++) {
        if (!inArr[i]) continue
        if (i === w) totals[i] += skinValue
        else totals[i] -= perLoser
      }
    }
  }
  return totals
}

// Per-player net/gross grid from the game's sparse scores node — matches
// settleWolf's scoreOf (gross ≤ 0 or missing → null; net subtracts the wolf
// stroke grid for the game's strokeMode when handicaps are on).
function buildGrid(players, scoresByHole, { handicapsOn, strokeMode, strokeIndex }) {
  const ids = players.map((p) => p.id)
  const grids = handicapsOn ? wolfStrokeGrid(players, strokeMode || 'lowest', strokeIndex) : null
  return players.map((_, pi) => Array.from({ length: 18 }, (_, h) => {
    const g = Number(scoresByHole?.[h + 1]?.[ids[pi]])
    if (!Number.isFinite(g) || g <= 0) return null
    const strokes = grids ? (grids[pi][h] || 0) : 0
    return g - strokes
  }))
}

// Full live/settled Skins state from a game object, or null when Skins is off or
// not viable (amount ≤ 0 or fewer than 2 IN). `holeOneBased` = the UI hole
// (1–18) for the live "pending" readout; omit (→ 19) for final settlement.
export function skinsState(game, holeOneBased = 19) {
  const sk = game?.meta?.addons?.skins
  if (!sk?.enabled) return null
  const players = game.players || []
  const names = players.map((p) => p.name)
  const inArr = players.map((p) => !sk.in || sk.in.includes(p.id))
  const inCount = inArr.filter(Boolean).length
  const amount = Number(sk.amount) || 0
  if (!(amount > 0) || inCount < 2) return null
  const course = game.meta.course || {}
  const grid = buildGrid(players, game.scores || {}, {
    handicapsOn: !!game.meta.handicapsOn, strokeMode: game.meta.options?.strokeMode, strokeIndex: course.strokeIndex,
  })
  const payout = sk.payout === 'per-player' ? 'per-player' : 'split-pot'
  const result = computeSkins(names, grid, holeOneBased - 1, inArr, !!sk.carry, !!sk.double)
  const nets = settleSkins(result, amount, inArr, payout)
  const rows = players.map((p, i) => ({ id: p.id, name: p.name, in: inArr[i], skins: result.counts[i], net: nets[i] }))
  return { players, inArr, inCount, amount, payout, carry: !!sk.carry, double: !!sk.double, useNet: !!game.meta.handicapsOn, result, nets, rows }
}
